import { Component, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';

import {
    DynamicHtmlDirective,
    LoadingIndicatorHandler,
    LoadingIndicatorService,
    NavigationService,
    PlainLinkComponent,
    UserLoginEvent,
    UserLogoutEvent,
    UserService,
} from '@frontend/vanilla/core';
import { PageMatrixComponent } from '@frontend/vanilla/features/content';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { CollapsibleContainerComponent } from './collapsible-container/collapsible-container.component';
import { PromoClientContentLoader } from './promo-client-content-loader.service';
import { PromoContentLoaderService } from './promo-content-loader.service';
import { PromoPageDomManipulatorService } from './promo-page-dommanipulator.service';
import { PromoPageContent, PromoPageDetails, PromoPageMetaData } from './promo.models';

@Component({
    selector: 'promo-content-loader',
    templateUrl: 'promo-content-loader.component.html',
    imports: [PlainLinkComponent, CollapsibleContainerComponent, PageMatrixComponent, DynamicHtmlDirective, RouterModule],
    providers: [PromoContentLoaderService, PromoPageDomManipulatorService, PromoClientContentLoader],
})
export class PromoContentLoaderComponent implements OnDestroy {
    markup: string = '';
    content: string;
    contentState: string;
    promoPageContent: PromoPageContent = new PromoPageContent();
    promoPageMetaData: PromoPageMetaData = new PromoPageMetaData();
    private readonly destroy$ = new Subject<void>();

    constructor(
        private loadingIndicator: LoadingIndicatorService,
        private navigation: NavigationService,
        private promoContentService: PromoContentLoaderService,
        private promoDomManipulator: PromoPageDomManipulatorService,
        private promoClientContentLoader: PromoClientContentLoader,
        private userService: UserService,
    ) {
        this.navigation.locationChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.loadContent();
        });
        this.userService.events
            .pipe(
                filter((e) => e instanceof UserLoginEvent || e instanceof UserLogoutEvent),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.loadContent();
            });
    }

    ngOnDestroy() {
        this.contentState = '';
        this.promoPageContent = {} as PromoPageContent;
        this.promoPageMetaData = {} as PromoPageMetaData;
        this.promoDomManipulator.removeCustomAssets();
        this.destroy$.next();
        this.destroy$.complete();
        //unsubscribe from events
        //Try to undo what this component has loaded
    }

    loadContent() {
        if (this.isPathToGetPreviewFrame(this.navigation.location.path())) {
            return;
        }
        const loadingIndicatorHandler = this.loadingIndicator.start();
        this.promoContentService
            .getPromoRenderedContentForCurrentPage()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.processSuccessHttpResponse(response.body, loadingIndicatorHandler);
                },
                error: (error: unknown) => {
                    this.processErrorHttpResponse(error, loadingIndicatorHandler);
                },
            });
    }

    // eslint:disable-next-line:typedef
    private isPathToGetPreviewFrame(path: string) {
        // getting index from where to remove the language code
        // eslint:disable-next-line:typedef
        const indexOfAreaStart = path.substr(1).indexOf('/') + 1;

        return path.substring(indexOfAreaStart) === '/promo/preview';
    }

    private processSuccessHttpResponse(promoPageDetailsObject: PromoPageDetails, loadingIndicatorHandler: LoadingIndicatorHandler): void {
        this.contentState = 'success';
        this.promoPageContent = promoPageDetailsObject.PromoPageRenderingFromSitecore;
        this.promoPageMetaData = promoPageDetailsObject.PromoPageMetaData;
        this.promoClientContentLoader.SetClientContentObject(promoPageDetailsObject.SerializedPromoChildContent);
        this.promoDomManipulator.manipulateDom(this.promoPageContent);
        loadingIndicatorHandler.done();
    }

    private processErrorHttpResponse(httpError: any, loadingIndicatorHandler: LoadingIndicatorHandler): void {
        if (httpError.status === 404) {
            this.contentState = 'notfound';
        }

        if (httpError.status === 500) {
            this.contentState = 'criticalError';
        }

        loadingIndicatorHandler.done();
    }
}
